import { Card, Flex, Skeleton } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useEffect, useRef } from "react";
import { useRevalidator, useRouteLoaderData } from "react-router";

import { useClientContext } from "~/contexts/client-context";
import { usePluginContext } from "~/contexts/plugin-context";
import type { loader } from "~/root";
import { createTheme } from "~/shared/utils/create-theme";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { useAuthModals } from "~/widgets/auth-modals";
import type {
  OTPRegistrationPluginProps,
  PluginInstance,
} from "~/widgets/otp-registration/types";

export const OTPRegistrationPlugin: FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const {
    affiliate,
    authFeatures,
    language: locale,
    siteSdk,
  } = useClientContext();
  const { gtm } = useGtmContext();

  const data = useRouteLoaderData<typeof loader>("root");
  const pluginSettings = data?.pluginIntegrations?.otpRegistration;
  const standaloneUrl = pluginSettings?.standalone_url ?? "";
  const { close, open } = useAuthModals();
  const pageDesignSystem = data?.pageDesignSystem;
  const revalidate = useRevalidator();
  const theme = createTheme(locale, {
    colors: pageDesignSystem?.colors ?? {},
    fontSize: pageDesignSystem?.font_size ?? {},
    fontWeight: pageDesignSystem?.font_weight ?? {},
    fontFamily: pageDesignSystem?.font_family ?? {},
    lineHeight: pageDesignSystem?.line_height ?? {},
    borderRadius: pageDesignSystem?.border_radius ?? {},
  });

  const userRedirectParams = {
    locale,
    page: "cashier",
    "pageParams[subPage]": "deposit",
  };
  const initialParams: OTPRegistrationPluginProps = {
    apiUrl:
      pluginSettings?.api_url ?? "https://casino-registration.jack-casino.com",

    referralCode: affiliate,
    onRegister: async (params) => {
      const { playerID } = params;
      await siteSdk.setAuthCookie(params);
      gtm.signUp({ eventTime: new Date(), playerID });
      console.info("register");
    },
    onGoToLogin: () => {
      close();
      open("login");
      console.info("go to login");
    },
    onLogin: async (params) => {
      close();
      const { playerID } = params;
      gtm.logIn({ eventTime: new Date(), playerID });
      const { redirectUrl } = await siteSdk.getRoomUrl(locale);
      console.info("login");
      window.location.href = redirectUrl;
    },
    onClose: async () => {
      console.info("on close");
      close();
      await revalidate.revalidate();
    },
    termsUrl: pluginSettings?.terms_url ?? "",
    textContent: {
      registration: {
        top_banner_description:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_top_banner_description,
        top_banner_image:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_top_banner_image,
        form_title:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_form_title,
        form_promo_code_input_label:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_form_promo_code_input_label,
        form_description_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_form_description_text,
        form_phone_input_label:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_form_phone_input_label,
        form_login_input_label:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_form_login_input_label,
        form_password_input_label:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_form_password_input_label,
        form_promo_code_button_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_form_promo_code_button_text,
        form_remember_checkbox_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_form_remember_checkbox_text,
        form_submit_button_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_form_submit_button_text,
        description_link_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_description_link_text,
        description_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_description_text,
        form_terms_description_label:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_form_terms_description_label,
        description_password_restore_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_description_password_restore_text,
        description_password_restore_link_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.registration_description_password_restore_link_text,
        validation_required:
          authFeatures?.registration?.[0]?.validation_required,
        validation_invalid_phone:
          authFeatures?.registration?.[0]?.validation_invalid_phone,
        validation_phone_max_length:
          authFeatures?.registration?.[0]?.validation_phone_max_length,
        validation_phone_min_length:
          authFeatures?.registration?.[0]?.validation_phone_min_length,
        validation_login_max_length:
          authFeatures?.registration?.[0]?.validation_nickname_max_length,
        validation_login_min_length:
          authFeatures?.registration?.[0]?.validation_nickname_min_length,
        validation_password_max_width:
          authFeatures?.registration?.[0]?.validation_password_max_width,
        validation_password_min_width:
          authFeatures?.registration?.[0]?.validation_password_min_width,
        validation_invalid_password:
          authFeatures?.registration?.[0]?.validation_invalid_password,
        validation_password_contain_spaces:
          authFeatures?.registration?.[0]?.validation_password_contain_spaces,
        validation_login_contain_password:
          authFeatures?.registration?.[0]?.validation_login_contain_password,
        validation_login_contains_only_numbers:
          authFeatures?.registration?.[0]
            ?.validation_login_contains_only_numbers,
      },
      otp: {
        form_title:
          pluginSettings?.otp_registration_translations?.[0]?.otp_form_title,
        form_submit_button_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.otp_form_submit_button_text,
        form_otp_input_label:
          pluginSettings?.otp_registration_translations?.[0]
            ?.otp_form_otp_input_label,
        form_description_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.otp_form_description_text,
        form_description_action_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.otp_form_description_action_text,
        form_notification_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.otp_form_notification_text,
        form_notification_action_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.otp_form_notification_action_text,
        validation_required:
          authFeatures?.registration?.[0]?.validation_required,
        validation_otp_length:
          authFeatures?.registration?.[0]?.validation_otp_length,
      },
      login: {
        form_title:
          pluginSettings?.otp_registration_translations?.[0]?.login_form_title,
        form_submit_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.login_form_submit_text,
        description_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.login_description_text,
        description_link_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.login_description_link_text,
        form_input_label_name:
          pluginSettings?.otp_registration_translations?.[0]
            ?.login_form_input_label_name,
        form_input_label_password:
          pluginSettings?.otp_registration_translations?.[0]
            ?.login_form_input_label_password,
        form_remember_checkbox_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.login_form_remember_checkbox_text,
        form_restore_link_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.login_form_restore_link_text,
        validation_required: authFeatures?.auth?.[0]?.validation_required,
        validation_invalid_credentials:
          authFeatures?.auth?.[0]?.validation_invalid_credentials,
      },
      restorePassword: {
        form_title:
          pluginSettings?.otp_registration_translations?.[0]
            ?.restore_password_form_title,
        form_description:
          pluginSettings?.otp_registration_translations?.[0]
            ?.restore_password_form_description,
        form_phone_input_label:
          pluginSettings?.otp_registration_translations?.[0]
            ?.restore_password_form_phone_input_label,
        form_submit_button_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.restore_password_form_submit_button_text,
        validation_required:
          authFeatures?.restore_password?.[0]?.validation_required,
        validation_invalid:
          authFeatures?.restore_password?.[0]?.validation_invalid,
        validation_phone_max_length:
          authFeatures?.registration?.[0]?.validation_phone_max_length,
        validation_phone_min_length:
          authFeatures?.registration?.[0]?.validation_phone_min_length,
        validation_invalid_phone:
          authFeatures?.registration?.[0]?.validation_invalid_phone,
      },
      createNewPassword: {
        form_input_label_confirm_password:
          pluginSettings?.otp_registration_translations?.[0]
            ?.create_new_password_form_input_label_confirm_password,
        form_input_label_password:
          pluginSettings?.otp_registration_translations?.[0]
            ?.create_new_password_form_input_label_password,
        form_submit_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.create_new_password_form_submit_text,
        form_title:
          pluginSettings?.otp_registration_translations?.[0]
            ?.create_new_password_form_title,
        form_description:
          pluginSettings?.otp_registration_translations?.[0]
            ?.create_new_password_form_description,
        form_otp_input_label:
          pluginSettings?.otp_registration_translations?.[0]
            ?.create_new_password_form_otp_input_label,
        form_notification_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.create_new_password_form_notification_text,
        form_notification_action_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.create_new_password_form_notification_action_text,
        form_description_action_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.create_new_password_form_description_action_text,
        form_description_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.create_new_password_form_description_text,
        validation_required:
          authFeatures?.restore_password?.[0]?.validation_required,
        validation_invalid_credentials:
          authFeatures?.restore_password?.[0]?.validation_invalid,
        validation_password_max_width:
          authFeatures?.registration?.[0]?.validation_password_max_width,
        validation_password_min_width:
          authFeatures?.registration?.[0]?.validation_password_min_width,
        validation_invalid_password:
          authFeatures?.registration?.[0]?.validation_invalid_password,
        validation_password_contain_spaces:
          authFeatures?.registration?.[0]?.validation_password_contain_spaces,
        validation_passwords_not_match:
          authFeatures?.restore_password?.[0]?.validation_passwords_not_match,
      },

      successRegistration: {
        form_title:
          pluginSettings?.otp_registration_translations?.[0]
            ?.success_registration_form_title,
        download_app_button_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.success_registration_download_app_button_text,
        form_submit_button_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.success_registration_form_submit_button_text,
        form_description_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.success_registration_form_description_text,
        form_description_action_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.success_registration_form_description_action_text,
        promo_image:
          pluginSettings?.otp_registration_translations?.[0]
            ?.success_registration_promo_image,
      },
      downloadInformationScreen: {
        info_image:
          pluginSettings?.otp_registration_translations?.[0]
            ?.download_information_info_image,
        form_submit_button_text:
          pluginSettings?.otp_registration_translations?.[0]
            ?.download_information_form_submit_button_text,
        form_description:
          pluginSettings?.otp_registration_translations?.[0]
            ?.download_information_form_description,
      },
    },
    locale,
    theme,
    onClickDownloadOrPlay: async (params, buttonType) => {
      if (buttonType === "download") {
        gtm.clickDownloadApp({
          eventTime: new Date(),
          playerID: params?.playerID,
        });
        const isMobile = window.matchMedia("(max-width: 768px)").matches;
        if (isMobile) {
          // скачивание на мобильном устройстве
          window.location.href =
            pluginSettings?.download_url ??
            "https://croreplay.com/cms-assets/0991d4de-3f63-403b-81af-6a5f08f110a2";
        }
      }

      if (buttonType === "playInWeb") {
        gtm.clickPlayInWeb({
          eventTime: new Date(),
          playerID: params?.playerID,
        });
        close();
        const { redirectUrl } = await siteSdk.getRoomUrlV2(userRedirectParams);
        window.location.href = redirectUrl;
      }
    },

    onShowSuccessRegistrationScreen: async (params) => {
      await siteSdk.setAuthCookie(params);
      // событие при открытии экрана с qr
      gtm.popupInstall({
        eventTime: new Date(),
        playerID: params?.playerID,
      });
    },

    onCloseDownloadInformationScreen: async (params) => {
      close();
      await siteSdk.setAuthCookie(params);
      const { redirectUrl } = await siteSdk.getRoomUrlV2(userRedirectParams);
      gtm.signUp({ eventTime: new Date(), playerID: params.playerID });
      console.info("onCloseDownloadInformationScreen");
      window.location.href = redirectUrl;
    },
  };

  const { loadScript, isLoading } = usePluginContext();

  useEffect(() => {
    if (!standaloneUrl) {
      return;
    }

    let instance: undefined | PluginInstance<OTPRegistrationPluginProps>;

    const init = async (): Promise<void> => {
      await loadScript(standaloneUrl);

      if (!ref.current) return;

      instance = window?.QWSFirstPlugin?.mount(ref.current, initialParams);
    };

    void init();
    return () => {
      instance?.unmount?.();
    };
  }, [ref, initialParams]);

  return isLoading ? (
    <Flex
      height={"100%"}
      flexDirection={"column"}
      justify={"center"}
      align={"center"}
    >
      <Card
        border={{ radius: "card" }}
        variant="default"
        bg="surface-primary-dark"
        md={{ maxWidth: 525 }}
        minWidth={312}
        maxWidth={440}
        d={{ maxWidth: 525, minWidth: 500 }}
      >
        <Skeleton radius={8} width={"100%"} height={580} />
      </Card>
    </Flex>
  ) : (
    <div ref={ref} id="otp-registration" />
  );
};
